import React from 'react'
import { Select } from 'antd'
import {
  CheckOutlined,
  CloseOutlined,
  PlusOutlined,
  SafetyOutlined,
} from '@ant-design/icons'
import type { PermissionDto, SubjectDto } from '../api/access-control.client'
import {
  allActionsFor,
  directActionsFor,
  formatLabel,
  MANAGE_ACTION,
} from '../constants'
import styles from './PermissionsEditor.module.scss'

interface Props {
  permissions: PermissionDto[]
  subjects: SubjectDto[]
  onChange: (next: PermissionDto[]) => void
  // When true the matrix is view-only: no toggles, no add/remove menu. Used for
  // a locked role (e.g. 'Platform Admin', 'RBAC Admin') whose access cannot be
  // edited, for a fixed-purpose role ('US-Restricted Data Access'), and for
  // viewers who lack Manage on PermissionManagement.
  readOnly?: boolean
  readOnlyReason?: 'locked' | 'fixed' | 'viewOnly'
}

function hasManage(actions: string[]): boolean {
  return actions.some((a) => a.toLowerCase() === MANAGE_ACTION.toLowerCase())
}

export default function PermissionsEditor({
  permissions,
  subjects,
  onChange,
  readOnly = false,
  readOnlyReason = 'locked',
}: Props) {
  const grantedCount = permissions.reduce((sum, p) => {
    const real = directActionsFor(subjects, p.subjectId)
    if (hasManage(p.actions)) return sum + real.length
    return sum + p.actions.filter((a) => real.some((r) => r === a)).length
  }, 0)

  function toggleAction(menu: string, action: string) {
    if (readOnly) return
    const isManage = action.toLowerCase() === MANAGE_ACTION.toLowerCase()
    const next = permissions.map((p) => {
      if (p.subjectId !== menu) return p
      // Manage covers every action, so it can't coexist with per-action grants.
      // Turning it on canonicalizes to just [Manage]; turning it off clears the
      // menu. This keeps the saved payload consistent with "Manage covers all".
      // Match Manage case-insensitively (hasManage does too) so permissions
      // loaded from the API with different casing can still be toggled off.
      if (isManage) {
        return { ...p, actions: hasManage(p.actions) ? [] : [MANAGE_ACTION] }
      }
      const on = p.actions.includes(action)
      return {
        ...p,
        actions: on
          ? p.actions.filter((a) => a !== action)
          : [...p.actions, action],
      }
    })
    onChange(next)
  }

  function addMenu(menu: string) {
    if (readOnly) return
    if (permissions.some((p) => p.subjectId === menu)) return
    onChange([...permissions, { subjectId: menu, actions: [] }])
  }

  function removeMenu(menu: string) {
    if (readOnly) return
    onChange(permissions.filter((p) => p.subjectId !== menu))
  }

  const addOptions = readOnly
    ? []
    : subjects
        .map((s) => s.subjectId)
        .filter((m) => !permissions.some((p) => p.subjectId === m))

  return (
    <div>
      <div className={styles.head}>
        <div>
          <h3 className={styles.title}>Permissions</h3>
          <p className={styles.subtitle}>
            {readOnly && readOnlyReason === 'viewOnly' ? (
              <>
                You have <strong>view-only</strong> access. Contact an RBAC
                Admin to change this role’s permissions.
              </>
            ) : readOnly && readOnlyReason === 'fixed' ? (
              <>
                This role’s access is <strong>locked</strong>. It only grants
                access to US-restricted member data. Assign or remove users on
                the <strong>Users</strong> tab to control who has it.
              </>
            ) : readOnly ? (
              <>
                This role’s access is <strong>locked</strong> and cannot be
                edited — it must keep full control of every menu so no one is
                ever locked out of access control.
              </>
            ) : (
              <>
                Choose what this role can do. Tap a pill to grant or revoke an
                action. <strong>Manage</strong> gives full control of a menu —
                it covers every action below it.
              </>
            )}
          </p>
        </div>
        <span className={styles.grantedBadge}>
          <CheckOutlined />
          {grantedCount} actions granted
        </span>
      </div>

      {permissions.length === 0 ? (
        <div className={styles.cards}>
          <div className={styles.empty}>
            {readOnly
              ? 'No menus granted.'
              : 'No menus yet. Add a menu below to grant access.'}
          </div>
        </div>
      ) : (
        <div className={styles.cards}>
          {permissions.map((perm) => {
            const menu = perm.subjectId
            const manageOn = hasManage(perm.actions)
            const real = directActionsFor(subjects, menu)
            const grantedReal = manageOn
              ? real.length
              : perm.actions.filter((a) => real.some((r) => r === a)).length
            const summary = manageOn
              ? 'Full access'
              : `${grantedReal} of ${real.length} granted`
            const allActions = allActionsFor(subjects, menu)
            return (
              <div key={menu} className={styles.card}>
                <div className={styles.cardHead}>
                  <span className={styles.menuName}>{formatLabel(menu)}</span>
                  <div className={styles.cardMeta}>
                    <span className={styles.summary}>{summary}</span>
                    {!readOnly && (
                      <button
                        type="button"
                        title="Remove menu"
                        className={styles.removeBtn}
                        onClick={() => removeMenu(menu)}
                      >
                        <CloseOutlined style={{ fontSize: 14 }} />
                      </button>
                    )}
                  </div>
                </div>
                <div className={styles.pillRow}>
                  {allActions.map((action) => {
                    if (action === MANAGE_ACTION) {
                      return (
                        <button
                          type="button"
                          key={action}
                          disabled={readOnly}
                          className={`${styles.pill} ${manageOn ? styles.pillManageOn : styles.pillManageOff}`}
                          onClick={() => toggleAction(menu, MANAGE_ACTION)}
                        >
                          <SafetyOutlined style={{ fontSize: 13 }} />
                          Manage
                        </button>
                      )
                    }
                    if (manageOn) {
                      return (
                        <span
                          key={action}
                          className={`${styles.pill} ${styles.pillCovered}`}
                        >
                          <CheckOutlined style={{ fontSize: 13 }} />
                          {formatLabel(action)}
                        </span>
                      )
                    }
                    const on = perm.actions.includes(action)
                    return (
                      <button
                        type="button"
                        key={action}
                        disabled={readOnly}
                        className={`${styles.pill} ${on ? styles.pillOn : styles.pillOff}`}
                        onClick={() => toggleAction(menu, action)}
                      >
                        {on ? (
                          <CheckOutlined style={{ fontSize: 13 }} />
                        ) : (
                          <PlusOutlined style={{ fontSize: 13 }} />
                        )}
                        {formatLabel(action)}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {addOptions.length > 0 && (
        <Select
          className={styles.addMenu}
          placeholder="+ Add a menu…"
          value={null}
          onChange={(menu: string) => addMenu(menu)}
          options={addOptions.map((m) => ({
            value: m,
            label: formatLabel(m),
          }))}
        />
      )}
    </div>
  )
}
